import React, { memo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
	setIsEditProject,
	setIsShowCreateProjectModal,
	setIsShowDeleteProjectModal,
	setSelectedProjectIdToDelete,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';
import {
	getDaysCount,
	getRandomColor,
	isProjectMember,
	isProjectOwner
} from '@features/project-management-tools/utils';

import CommonImage from '@/components/CommonImage';
import { getUserInfo, isAdmin, onGetLocalFromUtc } from '@/utils/Util';
import useClickOutside from '@/hooks/useClickOutside';
import CustomTooltip from '@/components/CustomTooltip';

const ProjectCard = ({ projectInfo, isDragging, isDetailsPage = false, onTrackMixPanelEvent }) => {
	const [clickActionButtonId, setClickActionButtonId] = useState(null);
	const dropdownRef = useRef(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loggedInUserId = getUserInfo()?.id;

	useClickOutside(dropdownRef, () => {
		setClickActionButtonId(null);
	});

	const handleEditProject = () => {
		dispatch(setIsEditProject(true));
		dispatch(setIsShowCreateProjectModal(true));
		dispatch(setSelectedProjectToEdit(projectInfo));
		setClickActionButtonId(null);
	};

	const handleDeleteProject = () => {
		dispatch(setIsShowDeleteProjectModal(true));
		dispatch(setSelectedProjectIdToDelete(projectInfo?.id));
		setClickActionButtonId(null);
	};

	const handleCardClick = () => {
		if (isDetailsPage) return;

		if (!isDragging) {
			navigate(`/my-project/${projectInfo?.id}`);
			onTrackMixPanelEvent && onTrackMixPanelEvent(projectInfo?.id, projectInfo?.name);
		}
	};

	const cardStyle = {
		cursor: isDragging ? 'grabbing' : 'pointer'
	};

	const isAllowedToDelete =
		isAdmin() ||
		isProjectOwner(projectInfo?.ownerResponse?.id, loggedInUserId) ||
		isProjectMember(projectInfo?.memberResponseList?.id, loggedInUserId);

	return (
		<div className="single-task-box mb-3" onClick={handleCardClick} style={cardStyle}>
			<div className="header-and-dropdown d-flex align-items-center justify-content-between">
				<div
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
					}}
				>
					<CustomTooltip text={projectInfo?.name}>
						<p className="gray_dark_01 regular-16 weight-500 cursor-pointer">
							{' '}
							{projectInfo?.name?.slice(0, 30)}
							{projectInfo?.name?.length > 30 && '...'}
						</p>
					</CustomTooltip>
				</div>
				{isDetailsPage && projectInfo?.noOfUnreadMessage > 0 && (
					<div className="unread-message">
						<p className="weight-500">{projectInfo?.noOfUnreadMessage}</p>
					</div>
				)}
				{!isDetailsPage && (
					<div className="option">
						<div className="dropdown" ref={dropdownRef}>
							<button
								className="btn btn-default cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setClickActionButtonId(projectInfo?.id);
								}}
							>
								<CommonImage src="/icons/more_icon.svg" alt="more" />
							</button>
							{clickActionButtonId === projectInfo?.id && (
								<ul
									className="dropdown-menu dropdown-menu-right shadow-lg show"
									style={{ position: 'absolute', right: 0, zIndex: 1000 }}
								>
									<li role="presentation" className="px-4 pb-3 pt-3">
										<span
											onClick={(e) => {
												e.stopPropagation();
												handleEditProject();
											}}
											className="font-weight-normal text-black"
										>
											Edit
										</span>
									</li>
									{isAllowedToDelete && (
										<li role="presentation" className="px-4 pb-3">
											<span
												className="font-weight-normal text-black"
												onClick={(e) => {
													e.stopPropagation();
													handleDeleteProject();
												}}
											>
												Delete
											</span>
										</li>
									)}
								</ul>
							)}
						</div>
					</div>
				)}
			</div>
			<div className="task-box-content mt-2">
				<ul>
					<li className="medium-14 gray_dark_02 weight-500 pb-1">
						Owner:
						<span className="gray_dark_01 ml-1">
							{projectInfo?.ownerResponse?.name || 'Unassigned'}
						</span>
					</li>
					{(projectInfo?.startDate || projectInfo?.dueDate) &&
						(projectInfo?.endDate || projectInfo?.dueDate) && (
							<li className="medium-14 gray_dark_02 weight-500">
								Timeline:
								<span className="gray_dark_01 ml-1">
									{onGetLocalFromUtc(projectInfo?.startDate, 'DD MMM')} -{' '}
									{onGetLocalFromUtc(
										projectInfo?.endDate || projectInfo?.dueDate,
										'DD MMM'
									)}{' '}
									{(() => {
										const days = getDaysCount(
											projectInfo?.startDate,
											projectInfo?.endDate || projectInfo?.dueDate
										);
										return !isNaN(days) ? `(${days} days)` : '';
									})()}
								</span>
							</li>
						)}
					{isDetailsPage && (
						<li className="medium-14 gray_dark_02 weight-500 pb-1">
							Priority:{' '}
							{projectInfo?.taskPriorityTagResponse?.text && (
								<span className="gray_dark_01 ml-1">
									{projectInfo?.taskPriorityTagResponse?.text}
								</span>
							)}
						</li>
					)}
				</ul>
				{!isDetailsPage && (
					<div className="progress-info task-progress-info mt-2 d-flex align-items-center justify-content-start">
						<div className="progress progress-square">
							<div
								className="progress-bar"
								style={{
									width: `${(projectInfo?.noOfCompletedTask / projectInfo?.noOfTotalTask) * 100}%`
								}}
							></div>
						</div>
						<div className="progress-text ml-2">
							<p className="regular-14 gray_dark_01">
								{projectInfo?.noOfCompletedTask}/{projectInfo?.noOfTotalTask}
							</p>
						</div>
					</div>
				)}
				<div className="add-member-and-unread-count d-flex align-items-center justify-content-between">
					{!isDetailsPage && (
						<div className="add-member add-buyer">
							<div className="added-members px-0">
								{projectInfo?.memberResponseList?.slice(0, 3)?.map((member) => (
									<React.Fragment key={member?.id}>
										{member?.profilePicDocUrl ? (
											<CommonImage
												src={member?.profilePicDocUrl}
												alt="alave"
												data-toggle="tooltip"
												data-placement="top"
												data-original-title={member?.name}
												isStatic={member?.profilePicDocUrl ? false : true}
												fallbackSrc="/images/pro_pic_default.svg"
											/>
										) : (
											<div className="img">
												<span
													className="regular-14 white name-icon"
													style={{
														background: `${getRandomColor(member?.name?.slice(0, 1))}`
													}}
												>
													{member?.name?.slice(0, 1)}
												</span>
											</div>
										)}
									</React.Fragment>
								))}

								{projectInfo?.memberResponseList?.length > 3 && (
									<div className="more-people">
										<span>+1</span>
									</div>
								)}
							</div>
						</div>
					)}
					{!isDetailsPage && projectInfo?.noOfUnreadMessage > 0 && (
						<div className="unread-message">
							<p className="weight-500">{projectInfo?.noOfUnreadMessage}</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default memo(ProjectCard);
